<template>
    <div ref="addToCartModal"
         class="modal-wrapper"
         id="modal-wrapper"
         @click="closeModalOutside($event)">
        <div v-cloak
             v-if="loading"
             class="processing">
            {{ $t('Loading') }}...
        </div>
        <div v-else
             id="modal"
             class="modal">
            <button class="btn--close"
                    @click="closeModal()">
                <span class="fas fa-times"></span>
            </button>

            <div v-cloak
                 v-if="priceData && !error"
                 class="grid grid-cols-2 gap-4">
                <div class="col-span-12 nm:col-span-6 panel">
                    <div class="panel__header">{{ $t('Prijs') }}</div>
                    <div class="panel__body text-20 font-medium">
                        <div class="flex items-center">
                            <span class="mr-2">{{ currencySymbol }}</span>
                            <input v-model="currentPrice"
                                   @blur="handlePriceInputBlur"
                                   class="w-full font-medium border-light border-b"
                                   min="0"
                                   step="0.01"
                                   type="number" />
                            <span aria-hidden="true"
                                  class="fas fa-pen text-14 ml-4 text-primary"></span>
                        </div>
                        <label class="text-16"
                               for="free-item">
                            <input id="free-item"
                                   v-model="freeItem"
                                   type="checkbox" />
                            {{ $t("Gratis product") }}
                        </label>
                    </div>
                </div>

                <div class="col-span-12 nm:col-span-6 panel">
                    <div class="panel__header">{{ $t('Hoeveelheid') }}</div>
                    <div class="panel__body text-20 flex items-center">
                        <input v-model="currentQty"
                               :min="getMinQty(product.multiples)"
                               :step="getStepQty(product.multiples)"
                               class="w-full font-medium border-light border-b"
                               type="number"
                               @blur="updateQty($event, product)"
                               @change="updateQty($event, product)"
                               @input="updateQty($event, product)" />
                        <span aria-hidden="true"
                              class="fas fa-pen text-14 ml-4 text-primary"></span>
                    </div>
                </div>

                <div class="col-span-12 nm:col-span-6 panel">
                    <div class="panel__body">
                        <table class="w-full table-alt">
                            <template v-for="(relation, relationName) in priceData.prices">
                                <tr v-for="(price, index) in relation">
                                    <td class="text-primary">{{ index === 0 ? relationName : '' }}</td>
                                    <td>{{ formatCurrency(price.amount) }}</td>
                                    <td v-if="price.quantityAmountFrom === price.quantityAmountTo"
                                        class="text-grey-300 font-medium">
                                        <span v-if="relation.length > 1">{{ price.quantityAmountFrom }}</span>
                                        <span v-else>-</span>
                                    </td>
                                    <td v-else
                                        class="text-grey-300 font-medium">
                                        {{ price.quantityAmountFrom }} -
                                        <span v-if="parseFloat(price.quantityAmountTo) === 0">&infin;</span>
                                        <span v-else>{{ price.quantityAmountTo == 99999 ? '&infin;' : price.quantityAmountTo }}</span>
                                    </td>
                                    <td class="text-right">
                                        <button @click="currentPrice = price.amount"><span aria-hidden="true" class="fas fa-plus-circle text-primary hover:text-secondary dt-colors fill-current ml-2"></span></button>
                                    </td>
                                </tr>
                            </template>
                        </table>
                    </div>
                </div>

                <div class="col-span-12 nm:col-span-6 panel">
                    <div class="panel__body">
                        <table class="w-full table-alt">
                            <tr>
                                <td class="text-primary">{{ $t('Artikel') }}</td>
                                <td>{{ product.sku }}</td>
                            </tr>
                            <tr>
                                <td class="text-primary">{{ $t('Eenheid') }}</td>
                                <td>{{ priceData.info.unit }}</td>
                            </tr>
                            <tr>
                                <td class="text-primary">{{ $t('Pallet') }}</td>
                                <td>{{ priceData.info.pallet }}</td>
                            </tr>
                            <tr>
                                <td class="text-primary">{{ $t('ProductenPerDoos') }}</td>
                                <td>{{ priceData.info.box }}</td>
                            </tr>
                            <tr>
                                <td class="text-primary">{{ $t('AantalPerOmverpakking') }}</td>
                                <td>{{ priceData.info.outerPackage }}</td>
                            </tr>
                        </table>
                    </div>
                </div>

                <div class="col-span-12 panel">
                    <div class="panel__header">{{ $t('Opmerking') }}</div>
                    <div class="panel__body flex items-start">
                        <span aria-hidden="true"
                              class="fas fa-pen text-14 mr-4 text-primary"></span>
                        <textarea v-model="currentRemark"
                                  class="w-full font-medium border-light border-b"
                                  rows="1"></textarea>
                    </div>
                </div>

                <button id="add-to-cart-btn"
                        class="btn btn--fill col-span-12"
                        @click="addToCart()">
                    <span aria-hidden="true"
                          class="fas fa-shopping-basket mr-2"></span>
                    {{ $t('Toevoegen') }}
                </button>
            </div>
            <p v-cloak
               v-else
               class="text-red-500 text-center">{{ error }}</p>
        </div>
    </div>
</template>

<script>
import {helpers} from "@/js/mixins/helpers.js";
import { enableScrollLock, disableScrollLock } from "../js/helpers/scroll-lock";

export default {
    name: "AddToCartModal",

    mixins: [helpers],

    data() {
        return {
            loading: false,
            error: null,
            priceData: null,
            priceTemp: 0,
            oldPrice: 0,
            currentQty: 1,
            currentRemark: "",
            product: null,
            freeItem: false
        }
    },

    watch: {
        freeItem: function (newValue) {
            if (newValue) {
                this.oldPrice = this.currentPrice
                this.currentPrice = '0.00' // 0 is not saved by Craft, 0.00 is
            } else {
                this.currentPrice = this.oldPrice
            }
        }
    },

    mounted() {
        enableScrollLock();
        
        this.product = this.$store.getters.getModalData('addToCart')

        this.loadData()
        // Set multiples
        if (this.currentQty === 1) {
            if (this.product.multiples) {
                const inventSiteId = this.user?.inventSiteId ?? ''

                const specificMultiple = this.product.multiples.filter(el => el.inventSiteId == inventSiteId)
                const globalMultiple = this.product.multiples.filter(el => el.inventSiteId == '' || el.inventSiteId == null)

                if (specificMultiple.length) {
                    this.currentQty = (specificMultiple[0].lowestQty > 0) ? specificMultiple[0].lowestQty : 1
                }

                if (globalMultiple.length) {
                    this.currentQty = (globalMultiple[0].lowestQty > 0) ? globalMultiple[0].lowestQty : 1
                }
            }
        }
    },

    methods: {
        getProductQty(id, multiples) {
            const product = this.isProductInOrder(id)
            if (product) return product.qty
            if (multiples) {
                // Check if customer is linked to one of the inventSiteId's
                // in the object
                const inventSiteId = this.user?.inventSiteId ?? ''

                const specificMultiple = multiples.filter(el => el.inventSiteId == inventSiteId)
                const globalMultiple = multiples.filter(el => el.inventSiteId == '' || el.inventSiteId == null)

                if (specificMultiple.length) {
                    return (specificMultiple[0].lowestQty > 0) ? specificMultiple[0].lowestQty : 1
                }

                if (globalMultiple.length) {
                    return (globalMultiple[0].lowestQty > 0) ? globalMultiple[0].lowestQty : 1
                }
            }
            return 1
        },

        getMinQty(multiples) {
            if (multiples) {
                // Check if customer is linked to one of the inventSiteId's
                // in the object
                const inventSiteId = this.user?.inventSiteId ?? ''

                const specificMultiple = multiples.filter((el) => el.inventSiteId == inventSiteId)
                const globalMultiple = multiples.filter((el) => el.inventSiteId == '' || el.inventSiteId == null)

                if (specificMultiple.length) {
                    return (specificMultiple[0].lowestQty > 0) ? specificMultiple[0].lowestQty : 1
                }

                if (globalMultiple.length) {
                    return (globalMultiple[0].lowestQty > 0) ? globalMultiple[0].lowestQty : 1
                }

            } else {
                return 1;
            }
        },

        getStepQty(multiples) {
            if (multiples) {
                // Check if customer is linked to one of the inventSiteId's
                // in the object
                const inventSiteId = this.user?.inventSiteId ?? ''

                const specificMultiple = multiples.filter((el) => el.inventSiteId == inventSiteId)
                const globalMultiple = multiples.filter((el) => el.inventSiteId == '' || el.inventSiteId == null)

                if (specificMultiple.length) {
                    return (specificMultiple[0].multipleQty > 0) ? specificMultiple[0].multipleQty : 1
                }

                if (globalMultiple.length) {
                    return (globalMultiple[0].multipleQty > 0) ? globalMultiple[0].multipleQty : 1
                }

            } else {
                return 1;
            }
        },

        updateQty(e, currentProduct, quantity) {
            const qty = e ? parseInt(e.target.value) : quantity;
            let id = currentProduct.hasOwnProperty('objectID') ? currentProduct.objectID : currentProduct.id
            // Now get the buttons in the form
            const addButton = document.getElementById('add-to-cart-btn')

            // Check for mandatory multiples
            if (currentProduct.multiples.length) {
                let step = parseInt(e.target.step)
                let min = parseInt(e.target.min)

                if (qty < min && qty === 0) {
                    this.$notify({
                        text: window.i18n.t("errors.minQtyError", {min}),
                        type: "error",
                    });

                    // Disable buttons
                    addButton.disabled = true

                    return false;
                }

                if (qty % step !== 0) {
                    this.$notify({
                        text: window.i18n.t("errors.stepQtyError", {step}),
                        type: "error",
                    });

                    // Disable buttons
                    addButton.disabled = true

                    return false;
                }
            }

            // Enabled buttons
            // Disable buttons
            addButton.removeAttribute('disabled')

        },
    
        closeModalOutside({target}) {
            const isPartOfModal = target.closest("#modal");
            if (isPartOfModal) return;
            this.closeModal();
        },

        closeModal() {
            disableScrollLock();
            
            this.$store.dispatch('toggleModal', {name: 'addToCart'})
            this.$emit('close-modal')
        },

        loadData() {
            if (this.product) {
                this.loading = true;

                // Set defaults
                this.currentPrice = this.product.defaultPrice
                this.currentQty = this.product.qty ?? 1

                window.axios({
                    url: '/actions/ranson-module/customer/price-info',
                    method: 'POST',
                    data: {sku: this.product.sku}
                }).then(result => {
                    if (result.status === 200 && result.data) {
                        if (result.data.success) {
                            if (result.data.priceData) {
                                if (!result.data.priceData.hasOwnProperty('info') && !result.data.priceData.hasOwnProperty('prices')) this.error = window.i18n.t("errors.noPriceInfoAvailable")

                                this.priceData = result.data.priceData
                                this.currentPrice = result.data.customerPrice
                                this.product.defaultPrice = result.data.customerPrice

                                let groupedPrices = this.priceData.prices.reduce((grouped, price) => {
                                    grouped[price.accountRelation] = [...grouped[price.accountRelation] || [], price];
                                    return grouped;
                                }, {});

                                // Remove keys that start with 0
                                for (const key of Object.keys(groupedPrices)) {
                                    if (key.startsWith('0')) delete groupedPrices[key]
                                }

                                this.priceData.prices = groupedPrices
                            } else {
                                this.error = window.i18n.t("errors.noPriceInfoFetched")
                            }
                        } else {
                            if (result.data.message) {
                                this.error = result.data.message
                            } else {
                                this.error = window.i18n.t("errors.noPriceInfoFetched")
                            }
                        }
                    } else {
                        this.error = window.i18n.t("errors.noPriceInfoFetched")
                        console.error(result);
                    }
                }).catch((error) => {
                    this.error = window.i18n.t("errors.noPriceInfoFetched")
                    console.error(error);
                }).finally(() => {
                    this.loading = false;
                });
            } else {
                this.error = window.i18n.t("errors.noProductSelected")
            }
        },

        addToCart() {
            if (this.product.purchasableId) {
                window.axios({
                    url: `${ window.rootUrl }actions/commerce/cart/update-cart`,
                    method: 'POST',
                    data: {
                        purchasableId: this.product.purchasableId,
                        qty: this.currentQty,
                        options: {
                            customPrice: this.currentPrice,
                            overrideSalesPrice: (parseFloat(this.currentPrice) !== parseFloat(this.product.defaultPrice)),
                            freeItem: this.freeItem
                        },
                        note: this.currentRemark
                    }
                }).then(result => {
                    if (result.status === 200) {
                        // Show notification
                        this.$notify({
                            text: window.i18n.t("success.addedToCart"),
                            type: "success",
                        });

                        // Save cart
                        if (result?.data?.cart) {
                            this.$store.dispatch('saveCart', result.data.cart);
                        }

                        // Show highlight products in cart
                        if (result?.data?.cart?.lineItems) {
                            const lineItems = result.data.cart.lineItems;
                            this.$emit('load-cart', result.data.cart.lineItems)
                        }

                        // Close modal
                        this.closeModal()

                        if (this.product.redirect) {
                            window.location = this.product.redirect
                        }

                    } else {
                        this.$notify({
                            title: window.i18n.t("errors.generalTitle"),
                            text: window.i18n.t("errors.notAddedToCart"),
                            type: "error",
                        });
                        console.error("Cart error.", result);
                    }
                }).catch((error) => {
                    this.$notify({
                        title: window.i18n.t("errors.generalTitle"),
                        text: window.i18n.t("errors.cartNotUpdated"),
                        type: "error",
                    });
                    console.error("Cart error.", error);
                });
            } else {
                this.$notify({
                    title: window.i18n.t("errors.generalTitle"),
                    text: window.i18n.t("errors.notAddedToCart"),
                    type: "error",
                });
                console.error("Cart error: no purchasable ID given.");
            }
        },

        handlePriceInputBlur(e) {
            if (!e.target.value) e.target.value = this.currentPrice
        }
    },

    computed: {
        currentPrice: {
            get: function () {
                return this.priceTemp
            },
            set: function (v) {
                if (v) {
                    const floatV = parseFloat(v)
                    // 0 is not saved by Craft, 0.00 is
                    this.priceTemp = floatV === 0 ? '0.00' : floatV
                }
            }
        }
    }
}
</script>

<style scoped>
.panel__body table td {
    @apply text-16;
}

.table-alt {
    td { @apply border-b border-light py-1.5; }
}
</style>
